// src/ui/controls/Joystick.tsx — nipplejs dynamic-mode joystick (MOVE-01/02/05).
//
// Dynamic mode: the stick spawns at the finger-landing point (D-17 / MOVE-01).
// 8 px tap-vs-drag deadzone (D-18 / MOVE-05) — distances at or below this
// threshold emit no Intent so taps stay taps.
// Release behaviour (D-13): facing holds at its last angle; velocity zeroes.
//
// Pushes Intent into the shared localInputController singleton (the sim side
// of the UI-to-sim seam — see src/sim/controllers/registry.ts). GameScene
// reads from the same singleton via its existing CharacterController.decide()
// loop — no Phaser imports enter the UI layer.
//
// nipplejs v1.0.1 changed its event handler signature: handlers registered via
// `collection.on(event, cb)` now receive a single InternalEvent<T> argument
// shaped as `{ type, target, data: T }` rather than the v0.x `(evt, data)`
// two-argument form.  We import the Collection type (return value of
// `nipplejs.create()`) and JoystickEventData (the T for 'move' events).
//
// Source: Phase 1 CONTEXT.md D-13/D-17/D-18, UI-SPEC.md §Interaction Contract,
// REQUIREMENTS.md MOVE-01/MOVE-02/MOVE-05.

import nipplejs from 'nipplejs';
import { useEffect, useRef, useState, type JSX } from 'react';

// nipplejs v1.0.1 does not re-export its internal class types from the module
// index. Derive the Collection type from the return value of nipplejs.create so
// TypeScript knows .on() and .destroy() exist without importing the unexported
// class directly.
type NippleCollection = ReturnType<typeof nipplejs.create>;

// Shape of the data field carried by InternalEvent<JoystickEventData> on
// 'move' events — only the fields this component actually reads.
interface MoveData {
  distance: number;
  force: number;
  angle: { radian: number };
}

import { localInputController } from '../../sim/controllers/registry';
import type { Intent, Vec2 } from '../../shared/types/intent';
import { getSettings, joystickSizePx, subscribe } from '../settings/store';

import './joystick.css';

/** Tap-vs-drag threshold in pixels. Exported for test traceability (MOVE-05 / D-18). */
export const JOYSTICK_DEADZONE_PX = 8;

/** Default actions for Intent pushed by the joystick. P1 has no sprint button (D-14). */
const DEFAULT_ACTIONS: Intent['actions'] = {
  sprint: false,
  toggleTorch: false,
  interact: false,
  shout: false,
  toggleHide: false,
  dropToFloor: false,
  peek: false,
};

export function Joystick(): JSX.Element {
  const zoneRef = useRef<HTMLDivElement | null>(null);
  // Holds the last non-zero facing angle so 'end' preserves it (D-13).
  const lastFacingRef = useRef<number>(0);

  // P7 Plan 10 (UI-06): a settings change must re-spawn the nipplejs manager at
  // the new size. We bump this counter from the settings-store subscription so
  // the create-effect (keyed on `sizeBump`) re-runs: destroy + recreate. The
  // store self-hydrates from `hunter:settings` at import, so the first spawn
  // already reflects the persisted size with no boot flash.
  const [sizeBump, setSizeBump] = useState<number>(0);

  useEffect(() => {
    return subscribe(() => setSizeBump((n) => n + 1));
  }, []);

  useEffect(() => {
    const zone = zoneRef.current;
    if (!zone) return;

    const manager: NippleCollection = nipplejs.create({
      zone,
      mode: 'dynamic',
      // nipplejs default skin is acceptable for P1 — restyle deferred to P6 per
      // UI-SPEC.md §Interaction Contract Joystick / D-09 discretion note.
      color: '#e8a838',
      // UI-06: Small/Medium/Large → 96/120/144 px, read from the settings store.
      size: joystickSizePx(getSettings().joystickSize),
      restOpacity: 0.5,
    });

    // nipplejs v1.0.1: 'move' handler receives InternalEvent<JoystickEventData>
    // shaped as { type, target, data: JoystickEventData }.
    manager.on('move', (evt: { type: string; target: unknown; data: MoveData }) => {
      const data = evt.data;
      // MOVE-05 / D-18: swallow sub-deadzone wobble so a tap stays a tap.
      if (data.distance <= JOYSTICK_DEADZONE_PX) return;

      // nipplejs angle.radian: 0 = right, π/2 = up (screen-y-up convention).
      // Sim/Phaser uses screen coords with y-DOWN, so Intent.move.y gets -sin.
      const angle = data.angle.radian;
      const force = Math.min(data.force, 1); // clamp — force can exceed 1 at edges

      const move: Vec2 = {
        x: Math.cos(angle) * force,
        y: -Math.sin(angle) * force,
      };
      lastFacingRef.current = angle;

      const intent: Intent = {
        move,
        facing: angle,
        actions: DEFAULT_ACTIONS,
      };
      localInputController.setIntent(intent);
    });

    // 'end' handler receives InternalEvent<Joystick> but we don't use the data.
    manager.on('end', () => {
      // D-13: zero velocity, hold facing at its last observed angle.
      const intent: Intent = {
        move: { x: 0, y: 0 },
        facing: lastFacingRef.current,
        actions: DEFAULT_ACTIONS,
      };
      localInputController.setIntent(intent);
    });

    return () => {
      manager.destroy();
    };
    // sizeBump re-runs this effect on a joystick-size settings change so the
    // manager re-spawns at the new px size (destroy + recreate).
  }, [sizeBump]);

  return (
    <div
      ref={zoneRef}
      className="joystick-zone"
      data-testid="joystick-zone"
      // Inline style mirrors joystick.css so the Plan 01 test's
      // `style.touchAction` / `style.position` reads resolve under jsdom
      // (jsdom does not apply external stylesheets during render).
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 50,
        touchAction: 'none',
        pointerEvents: 'auto',
        background: 'transparent',
      }}
    />
  );
}
